import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faVideoCamera,
  faComments,
  faFileText,
  faAreaChart,
  faPause,
  faPlay,
  faTimes,
  faPen,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import patientService from '../../../services/patientService';
import socketService from '../../../services/socketService';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import BackButton from '../../../components/BackButton/BackButton';
import PageLoadingSpinner from '../../../components/PageLoadingSpinner/PageLoadingSpinner';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import { checkBPValues, checkBGValue, checkWeightValue } from '../../../utils/measurementUtils';
import { buildPatientProfileImageUrl } from '../../../utils/patientProfileImage.js';
import {
  resolveDisplayScheduleTargets,
  hasAnyScheduleTargets,
  normalizePeriodName
} from '../../../utils/scheduleTargetUtils';
import {
  getUsePatientScheduleTargetsPreference,
  setUsePatientScheduleTargetsPreference,
  PATIENT_SCHEDULE_TARGET_PREF_EVENT
} from '../../../utils/patientScheduleTargetPreference.js';
import pulseIcon from '../../../assets/pulse.png';
import './PatientDetails.css';

const DEFAULT_TARGETS = {
  bloodPressureTargetRange: [{
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90
  }],
  bloodGlucoseTargetRange: [{
    minimum: 60,
    maximum: 110
  }],
  weightTargetRange: {
    weightMin: 66,
    weightMax: 220
  }
};

export default function PatientDetails() {
  const { practiceId, patientId } = useParams();
  const navigate = useNavigate();
  const { updateBreadcrumbs } = useBreadcrumb();

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [practiceIsLocked] = useState(false);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const detailsCacheRef = useRef(new Map());
  const notesCacheRef = useRef(new Map());
  const [resolvedScheduleTargets, setResolvedScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [scheduleTargetPrefRevision, setScheduleTargetPrefRevision] = useState(0);

  const patientProfilePicUrl = useMemo(() => {
    const p = patientData?.patient || {};
    const rawProfile =
      p.profile_pic ||
      p.profilePic ||
      p.profile_image ||
      p.profileImage ||
      patientData?.profile_pic ||
      patientData?.profilePic ||
      patientData?.profile_image ||
      patientData?.profileImage ||
      null;
    return buildPatientProfileImageUrl(rawProfile);
  }, [patientData]);

  useEffect(() => {
    // Reset failure state whenever patient/profile image changes.
    setProfileImageFailed(false);
  }, [patientProfilePicUrl, patientData?.patient?.id, patientData?.patient?.patients_table_id]);

  // Meter management state
  const [patientMeters, setPatientMeters] = useState([]);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [addMeterForm, setAddMeterForm] = useState({
    model_number: '',
    extension: '',
    serial_number: '',
    gateway_serial_number: ''
  });

  // Appointment form state
  const [appointmentData, setAppointmentData] = useState({
    facility: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    provider: '',
    caregiver: '',
    visitType: '',
    visitStatus: ''
  });

  const fetchPatientNotes = useCallback(async (baseData) => {
    const notesPatientId =
      baseData?.patient?.patients_table_id ||
      baseData?.patient?.id ||
      patientId;
    const notesCacheKey = `${practiceId}:${notesPatientId}`;
    if (notesCacheRef.current.has(notesCacheKey)) {
      return notesCacheRef.current.get(notesCacheKey) || [];
    }
    const notesResponse = await patientService
      .getPatientNotes(practiceId, notesPatientId)
      .catch(() => null);
    const notes = notesResponse?.data?.success ? (notesResponse.data.data || []) : [];
    notesCacheRef.current.set(notesCacheKey, notes);
    return notes;
  }, [practiceId, patientId]);

  const fetchPatientDetails = useCallback(async ({ includeNotes = true, showLoader = true } = {}) => {
    try {
      const detailsCacheKey = `${practiceId}:${patientId}`;
      // Use memoized payload to avoid unnecessary refetches for same patient.
      // Useful in React StrictMode remounts and local re-renders.
      if (includeNotes && detailsCacheRef.current.has(detailsCacheKey)) {
        const cached = detailsCacheRef.current.get(detailsCacheKey);
        setPatientData(cached);
        if (showLoader) setLoading(false);
        return;
      }

      if (showLoader) setLoading(true);
      setError(null);
      const detailsResponse = await patientService.getPatientDetailsFast(practiceId, patientId);

      if (detailsResponse.data.success) {
        const baseData = detailsResponse.data.data || {};
        if (includeNotes) {
          const followUpNotes = await fetchPatientNotes(baseData);
          const next = {
            ...baseData,
            follow_up_notes: followUpNotes
          };
          detailsCacheRef.current.set(detailsCacheKey, next);
          setPatientData(next);
        } else {
          setPatientData((prev) => ({
            ...baseData,
            follow_up_notes: prev?.follow_up_notes || []
          }));
        }
      } else {
        setError(detailsResponse.data.message || 'Failed to load patient details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load patient details');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [practiceId, patientId, fetchPatientNotes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!practiceId || !patientId) return;
      try {
        const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
        if (cancelled) return;
        setScheduleTargetSources(sources);
        const usePatientTargets = getUsePatientScheduleTargetsPreference(
          practiceId,
          patientId,
          sources?.patientData
        );
        const effective = resolveDisplayScheduleTargets({
          practiceData: sources.practiceData,
          patientData: sources.patientData,
          usePatientTargets
        });
        setResolvedScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
      } catch {
        if (!cancelled) setResolvedScheduleTargets(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId, scheduleTargetPrefRevision]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const handlePrefChange = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setScheduleTargetPrefRevision((n) => n + 1);
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, handlePrefChange);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, handlePrefChange);
  }, [practiceId, patientId]);

  const usePatientSchedulePref = useMemo(
    () => getUsePatientScheduleTargetsPreference(practiceId, patientId, scheduleTargetSources?.patientData),
    [practiceId, patientId, scheduleTargetSources, scheduleTargetPrefRevision]
  );

  const applyScheduleTargetPreference = useCallback(
    (usePatient) => {
      if (!practiceId || !patientId || practiceIsLocked) return;
      const prev = getUsePatientScheduleTargetsPreference(
        practiceId,
        patientId,
        scheduleTargetSources?.patientData
      );
      if (prev === usePatient) return;
      setUsePatientScheduleTargetsPreference(practiceId, patientId, usePatient);
      toast.info(
        usePatient
          ? 'System will now use patient level target setting'
          : 'System will now use practice level target setting'
      );
      detailsCacheRef.current.delete(`${practiceId}:${patientId}`);
      try {
        sessionStorage.removeItem(`measurement:patient:${practiceId}:${patientId}`);
      } catch {
        /* ignore */
      }
      setScheduleTargetPrefRevision((n) => n + 1);
    },
    [practiceId, patientId, practiceIsLocked, scheduleTargetSources]
  );

  // Clear caches when patient context changes.
  useEffect(() => {
    detailsCacheRef.current.clear();
    notesCacheRef.current.clear();
  }, [practiceId, patientId]);

  useEffect(() => {
    fetchPatientDetails({ includeNotes: true, showLoader: true });
  }, [fetchPatientDetails]);

  useEffect(() => {
    const handleWindowFocus = () => {
      fetchPatientDetails({ includeNotes: false, showLoader: false });
    };
    const handleWeightMeasurementAdded = (event) => {
      const evtPracticeId = String(event?.detail?.practiceId || '');
      const evtPatientId = String(event?.detail?.patientId || '');
      if (evtPracticeId !== String(practiceId) || evtPatientId !== String(patientId)) return;
      detailsCacheRef.current.delete(`${practiceId}:${patientId}`);
      fetchPatientDetails({ includeNotes: false, showLoader: false });
    };
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('weight:measurement-added', handleWeightMeasurementAdded);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('weight:measurement-added', handleWeightMeasurementAdded);
    };
  }, [fetchPatientDetails]);

  // Socket.IO real-time updates
  useEffect(() => {
    // Connect to Socket.IO
    socketService.connect();

    // Join patient room for real-time updates
    if (practiceId && patientId) {
      socketService.joinPatientRoom(practiceId, patientId);
    }

    // Listen for vital reading updates
    const handleVitalUpdate = (data) => {
      console.log('Real-time vital reading update received:', data);

      // Show toast notification
      const measurementType = data.measurementType || 'measurement';
      const typeLabels = {
        'blood_pressure': 'Blood Pressure',
        'blood_glucose': 'Blood Glucose',
        'weight': 'Weight'
      };
      const label = typeLabels[measurementType] || 'Vital Reading';

      toast.success(`New ${label} reading received!`, {
        position: 'top-right',
        autoClose: 3000
      });

      // Refresh vitals/details only; keep notes untouched unless notes changed.
      fetchPatientDetails({ includeNotes: false, showLoader: false });
    };

    socketService.onVitalReadingUpdate(handleVitalUpdate);

    // Cleanup on unmount
    return () => {
      socketService.offVitalReadingUpdate();
      if (practiceId && patientId) {
        socketService.leavePatientRoom(practiceId, patientId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, patientId]);

  useEffect(() => {
    if (patientData && patientData.patient && !loading) {
      const patient = patientData.patient;
      const patientFullName = `${patient.last_name || ''}, ${patient.first_name || ''}`.trim();
      const practiceName = patient.practice_name || 'Practice';

      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: practiceName, path: `/practices/${practiceId}` },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient` },
        { label: patientFullName || 'Patient', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, loading, updateBreadcrumbs]);

  useEffect(() => {
    if (!practiceIsLocked) {
      setIsRunning(true);
    } else {
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const fetchPatientMeters = async () => {
    try {
      setLoadingMeters(true);
      const response = await patientService.getPatientMeters(practiceId, patientId);
      if (response.data.success) {
        setPatientMeters(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching patient meters:', error);
    } finally {
      setLoadingMeters(false);
    }
  };

  const handleAddMeter = async (e) => {
    e.preventDefault();
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }

    // Validation
    if (!addMeterForm.model_number || !addMeterForm.extension || !addMeterForm.serial_number) {
      toast.error('Please fill in all required fields (Model, Extension, Serial Number)');
      return;
    }

    try {
      const response = await patientService.addMeterToPatient(practiceId, patientId, addMeterForm);
      if (response.data.success) {
        toast.success('Meter added successfully');
        if (response.data.foracareSync) {
          if (response.data.foracareSync.reCode === '00') {
            toast.success('Meter synced with ForaCare');
          } else {
            toast.warning('Meter added but ForaCare sync failed');
          }
        }
        setShowAddMeterModal(false);
        setAddMeterForm({
          model_number: '',
          extension: '',
          serial_number: '',
          gateway_serial_number: ''
        });
        fetchPatientMeters(); // Refresh list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add meter');
      console.error('Error adding meter:', error);
    }
  };

  const handleDeleteMeter = async (meterId) => {
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }
    if (!window.confirm('Are you sure you want to remove this meter?')) {
      return;
    }

    try {
      const response = await patientService.deleteMeterFromPatient(practiceId, patientId, meterId);
      if (response.data.success) {
        toast.success('Meter removed successfully');
        if (response.data.foracareSync) {
          if (response.data.foracareSync.reCode === '00') {
            toast.success('Meter removed from ForaCare');
          } else {
            toast.warning('Meter removed but ForaCare sync failed');
          }
        }
        fetchPatientMeters(); // Refresh list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove meter');
      console.error('Error removing meter:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]}, ${date.getDate().toString().padStart(2, '0')} ${date.getFullYear()}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();

    // Preserve stored wall-clock time from DB strings like "YYYY-MM-DD HH:mm:ss"
    // and avoid browser timezone shifting that can alter displayed time.
    const localLike = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (localLike) {
      const year = Number(localLike[1]);
      const month = localLike[2];
      const day = localLike[3];
      const hours = Number(localLike[4]);
      const minutes = localLike[5];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${month}-${day}-${year}, ${displayHours}:${minutes} ${ampm}`;
    }

    const date = new Date(raw);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}-${day}-${year}, ${displayHours}:${minutes} ${ampm}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatDateMDY = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '-';
    return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const handleTimerMinutesChange = (value) => {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    const nextMinutes = cleaned === '' ? 0 : parseInt(cleaned, 10);
    const safeMinutes = Number.isFinite(nextMinutes) ? Math.max(0, nextMinutes) : 0;
    const currentSeconds = timer % 60;
    setTimer((safeMinutes * 60) + currentSeconds);
  };

  const handleTimerSecondsChange = (value) => {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    const nextSeconds = cleaned === '' ? 0 : parseInt(cleaned, 10);
    const safeSeconds = Number.isFinite(nextSeconds) ? Math.min(59, Math.max(0, nextSeconds)) : 0;
    const currentMinutes = Math.floor(timer / 60);
    setTimer((currentMinutes * 60) + safeSeconds);
  };

  const resetTimer = () => {
    setTimer(0);
    setIsRunning(false);
  };


  const getStatusBadge = (status) => {
    if (!status) {
      return <span className="patient-details-status-badge discontinue">N/A</span>;
    }

    // Normalize status to string for safe comparisons.
    const normalized = typeof status === 'number' ? String(status) : String(status).trim();
    const statusLower = normalized.toLowerCase();

    // Convert numeric codes -> alphabet labels
    // (matches PatientManagement: 2=Active, 3=Pending, 4=Locked)
    const numericStatus = /^\d+$/.test(statusLower) ? parseInt(statusLower, 10) : null;
    if (numericStatus !== null) {
      if (numericStatus === 2) {
        return <span className="patient-details-status-badge active">Active</span>;
      }
      if (numericStatus === 3) {
        // No dedicated CSS class for "pending", so reuse the "paused" color.
        return <span className="patient-details-status-badge paused">Pending</span>;
      }
      if (numericStatus === 4) {
        return <span className="patient-details-status-badge lock">Locked</span>;
      }
    }

    const statusMap = {
      'active': { class: 'active', text: 'Active' },
      'pending': { class: 'paused', text: 'Pending' },
      'paused': { class: 'paused', text: 'Paused' },
      'locked': { class: 'lock', text: 'Locked' },
      'discontinue': { class: 'discontinue', text: 'N/A' },
      'inactive': { class: 'discontinue', text: 'Inactive' }
    };
    const statusInfo = statusMap[statusLower] || { class: 'discontinue', text: status || 'N/A' };
    return <span className={`patient-details-status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getGenderText = (gender) => {
    if (gender === 1 || gender === '1' || String(gender).toLowerCase() === 'male') {
      return 'Male';
    }
    if (gender === 0 || gender === '0' || gender === 2 || gender === '2' || String(gender).toLowerCase() === 'female') {
      return 'Female';
    }
    if (gender === 3 || gender === '3' || String(gender).toLowerCase() === 'other') {
      return 'Other';
    }
    return 'N/A';
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || 'P';
  };

  const formatNameLastFirst = (fullName) => {
    const raw = String(fullName || '').trim();
    if (!raw) return '-';
    if (raw.includes(',')) return raw;
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0];
    const first = parts.shift();
    const last = parts.join(' ');
    return `${last}, ${first}`;
  };

  const getBPStatus = (bpData) => {
    if (!bpData) return { sysStatus: 'normal', diaStatus: 'normal' };
    const arr = resolvedScheduleTargets?.bloodPressureTargetRange;
    if (!Array.isArray(arr) || arr.length === 0) {
      return checkBPValues(bpData, DEFAULT_TARGETS.bloodPressureTargetRange[0]);
    }
    const periodLabel = bpData.period_name || bpData.period || '';
    let target = null;
    if (periodLabel) {
      const normalized = normalizePeriodName(periodLabel);
      target = arr.find((t) => {
        const tn = normalizePeriodName(t.name);
        return tn === normalized || tn.includes(normalized) || normalized.includes(tn);
      });
    }
    if (!target) target = arr[0];
    return checkBPValues(bpData, target);
  };

  const getBGStatus = (bgData) => {
    if (!bgData) return 'normal';
    const val = Number(
      bgData.blood_glucose_value_1 ??
      bgData.blood_glucose_value_2 ??
      bgData.blood_glucose_value_3 ??
      bgData.blood_glucose_value ??
      bgData.glucose_value
    );
    const arr = resolvedScheduleTargets?.bloodGlucoseTargetRange;
    if (!Array.isArray(arr) || arr.length === 0) {
      return checkBGValue(val, DEFAULT_TARGETS.bloodGlucoseTargetRange[0]);
    }
    const periodLabel = bgData.period_name || bgData.period || '';
    let target = null;
    if (periodLabel) {
      const normalized = normalizePeriodName(periodLabel);
      target = arr.find((t) => {
        const tn = normalizePeriodName(t.name || t.label);
        return tn === normalized || tn.includes(normalized) || normalized.includes(tn);
      });
    }
    if (!target) target = arr[0];
    return checkBGValue(val, target);
  };

  const getWeightStatus = (weightData) => {
    if (!weightData) return 'normal';
    const w = resolvedScheduleTargets?.weightTargetRange;
    const target =
      w && (w.weightMin != null || w.weightMax != null)
        ? w
        : DEFAULT_TARGETS.weightTargetRange;
    return checkWeightValue(Number(weightData.weight), target);
  };

  if (loading) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-main-container">
          <PageLoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-error-container">
          <div className="patient-details-error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!patientData || !patientData.patient) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-error-container">
          <div className="patient-details-error-message">No patient data available</div>
        </div>
      </div>
    );
  }

  const { patient, latest_measurements, average_measurements, follow_up_notes, caregivers, device_serial } = patientData;
  const bp = latest_measurements?.blood_pressure;
  const bg = latest_measurements?.blood_glucose;
  const weight = latest_measurements?.weight;
  const bgDisplayValue = bg
    ? Number(
      bg.blood_glucose_value_1 ??
      bg.blood_glucose_value_2 ??
      bg.blood_glucose_value_3 ??
      bg.blood_glucose_value ??
      bg.glucose_value
    )
    : null;

  const avgTabs = [
    {
      id: 'seven_days',
      title: '7 Days Average',
      data: average_measurements?.seven_days,
      start_date: average_measurements?.seven_days?.start_date,
      end_date: average_measurements?.seven_days?.end_date
    },
    {
      id: 'one_month',
      title: '1 Month Average',
      data: average_measurements?.one_month,
      start_date: average_measurements?.one_month?.start_date,
      end_date: average_measurements?.one_month?.end_date
    },
    {
      id: 'three_months',
      title: '3 Months Average',
      data: average_measurements?.three_months,
      start_date: average_measurements?.three_months?.start_date,
      end_date: average_measurements?.three_months?.end_date
    }
  ];

  const patientFullName = `${patient.last_name || ''}, ${patient.first_name || ''}`.trim();
  const practiceName = patient.practice_name || 'Practice';
  const isHighBP = bp && (bp.systolic_pressure > 120 || bp.diastolic_pressure > 80);
  const initials = getInitials(patient.first_name, patient.last_name);

  // Determine alert colors for last-upload vitals
  const bpStatus = getBPStatus(bp);
  const bgStatus = getBGStatus(bg);
  const weightStatus = getWeightStatus(weight);

  // Helper function to check if a vital is configured for monitoring
  const isVitalConfigured = (vitalType) => {
    // Check patient.vitals string (e.g., "BP, WT" or "BP, BG, WT")
    const vitals = patient.vitals || '';
    const vitalMap = {
      'BP': 'BP',
      'Blood Pressure': 'BP',
      'BG': 'BG',
      'Blood Glucose': 'BG',
      'WT': 'WT',
      'Weight': 'WT'
    };
    const vitalCode = vitalMap[vitalType] || vitalType;
    return vitals.includes(vitalCode);
  };

  // Determine if vitals are configured (enable cards even if no data)
  const isBPConfigured = isVitalConfigured('BP') || isVitalConfigured('Blood Pressure');
  const isBGConfigured = isVitalConfigured('BG') || isVitalConfigured('Blood Glucose');
  const isWeightConfigured = isVitalConfigured('WT') || isVitalConfigured('Weight');

  const getSummaryRange = (tabId) => {
    const endDate = new Date();
    const startDate = new Date(endDate);

    if (tabId === 'seven_days') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (tabId === 'one_month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else if (tabId === 'three_months') {
      startDate.setMonth(startDate.getMonth() - 3);
    }

    return {
      start: formatDateMDY(startDate),
      end: formatDateMDY(endDate)
    };
  };

  const caregiverDisplayValue = caregivers && caregivers.length > 0
    ? caregivers.map((cg) => formatNameLastFirst(cg.full_name || `${cg.last_name || ''} ${cg.first_name || ''}`)).join(', ')
    : formatNameLastFirst(patient.caregiver_name);

  return (
    <div className="patient-details-page">
      {/* Main Container */}
      <div className="patient-details-main-container">

        {/* Top Full-Width EHR Patient Banner Card */}
        <div className="patient-details-unified-card">
          {/* Left Column: Profile */}
          <div className="patient-details-profile-section">
            <div className="patient-details-profile-header">
              {patientProfilePicUrl && !profileImageFailed ? (
                <img
                  src={patientProfilePicUrl}
                  alt="Profile"
                  className="patient-details-avatar-img"
                  onError={() => setProfileImageFailed(true)}
                />
              ) : null}
              <div className="patient-details-avatar-circle" style={{ display: patientProfilePicUrl && !profileImageFailed ? 'none' : 'flex' }}>
                {initials}
              </div>
              <div className="patient-details-profile-info">
                <div className="patient-details-profile-name-row">
                  <h2 className="patient-details-patient-name-responsive">{patientFullName || 'Patient'}</h2>
                  <a
                    href="#"
                    className="patient-details-edit-btn-link"
                    title="Edit"
                    onClick={(e) => {
                      e.preventDefault();
                      const pid = patient.patients_table_id ?? patient.id ?? patientId;
                      navigate(`/practices/${practiceId}/patients/${pid}/edit`);
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </a>
                </div>

                <div className="patient-details-contact-rows">
                  <p className="patient-details-info-row">
                    <FontAwesomeIcon icon={faPhone} className="fu-info-icon" />
                    <span className="patient-details-info-text-bold">{patient.phone_number || '-'}</span>
                  </p>
                  <p className="patient-details-info-row">
                    <FontAwesomeIcon icon={faEnvelope} className="fu-info-icon" />
                    <span className="patient-details-info-text-bold">{patient.email || '-'}</span>
                  </p>
                  <p className="patient-details-info-row">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="fu-info-icon" />
                    <span className="patient-details-info-text-bold">
                      {patient.full_address || patient.address_line_1 || ''}
                      {patient.city && `, ${patient.city}`}
                      {patient.zip_code && ` ${patient.zip_code}`}
                    </span>
                  </p>
                </div>

                <div className="patient-details-status-row">
                  <span>Status:</span>
                  {getStatusBadge(patient.status)}
                </div>
              </div>
            </div>
            <div className="patient-details-profile-actions">
              <button
                className="list-screen-add-btn"
                onClick={() => setShowAppointmentModal(true)}
              >
                Book Appointment
              </button>
              <button className="patient-details-btn-icon" disabled>
                <FontAwesomeIcon icon={faPhone} />
              </button>
              <button className="patient-details-btn-icon" disabled>
                <FontAwesomeIcon icon={faVideoCamera} />
              </button>
              <button className="patient-details-btn-icon" disabled>
                <FontAwesomeIcon icon={faComments} />
              </button>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="patient-details-unified-divider"></div>

          {/* Right Column: Details Grid */}
          <div className="patient-details-details-section">
            <div className="patient-details-details-grid">
              <DetailItem label="User ID" value={patient.user_id || '-'} />
              <DetailItem label="Patient ID" value={patient.patient_id || '-'} />
              <DetailItem label="DOB" value={patient.date_of_birth ? formatDate(patient.date_of_birth) : '-'} />
              <DetailItem label="Gender" value={getGenderText(patient.gender)} />
              <DetailItem label="Practice" value={practiceName} />
              <DetailItem label="Provider" value={formatNameLastFirst(patient.provider_name)} />
              <DetailItem
                label="Caregiver"
                value={caregiverDisplayValue}
              />
              <DetailItem label="Device Serial" value={device_serial || '-'} />
              <DetailItem
                label="Height"
                value={`${patient.height_in_feet || '00'} feet, ${patient.height_in_inches || '00'} inches`}
              />
              <DetailItem
                label="Weight"
                value={patient.weight_in_lbs ? `${parseFloat(patient.weight_in_lbs).toFixed(2)} lb` : '-'}
              />
              <DetailItem
                label="BMI"
                value={patient.bmi ? parseFloat(patient.bmi).toFixed(2) : '-'}
              />
            </div>
          </div>
        </div>

        {/* Content 2-Column Layout (Left: Vitals/Tabs / Right: Timer & Notes) */}
        <div className="patient-details-content-grid">
          {/* Left Column */}
          <div className="patient-details-left-column">
            <div className="patient-details-card patient-details-target-range-card">

              <h3>Vital target ranges</h3>
              <div className="patient-details-target-checkboxes" role="group" aria-label="Schedule target source">
                <label className="patient-details-target-checkbox-label">
                  <input
                    type="checkbox"
                    checked={!usePatientSchedulePref}
                    disabled={practiceIsLocked}
                    onChange={() => applyScheduleTargetPreference(false)}
                  />
                  <span>Practice level schedule target setting</span>
                </label>
                <label className="patient-details-target-checkbox-label">
                  <input
                    type="checkbox"
                    checked={usePatientSchedulePref}
                    disabled={practiceIsLocked}
                    onChange={() => applyScheduleTargetPreference(true)}
                  />
                  <span>Patient schedule target setting</span>
                </label>
              </div>
            </div>

            {/* Last Upload Data */}
            <div className="patient-details-card">
              <h3>Last Upload Data</h3>
              <div className="patient-details-vitals-grid">
                <VitalCard
                  title="Blood Pressure"
                  date={bp ? formatDateTime(bp.created_at || bp.measure_new_date_time || bp.measure_date_time) : ''}
                  systolic={bp ? Math.round(bp.systolic_pressure) : null}
                  diastolic={bp ? Math.round(bp.diastolic_pressure) : null}
                  unit="mmHg"
                  pulse={bp?.pulse ? Math.round(bp.pulse) : null}
                  bpStatus={bpStatus}
                  noData={!bp}
                  isConfigured={isBPConfigured}
                  onFirstIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`);
                  }}
                  onSecondIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/chart`);
                  }}
                />
                <VitalCard
                  title="Blood Glucose"
                  date={bg ? formatDateTime(bg.measure_new_date_time || bg.measure_date_time || bg.created_at) : ''}
                  value={bgDisplayValue != null && !Number.isNaN(bgDisplayValue) ? Math.round(bgDisplayValue) : '-'}
                  unit="mg/dl"
                  pulse={null}
                  alertStatus={bgStatus}
                  noData={!bg}
                  isConfigured={isBGConfigured}
                  onFirstIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`);
                  }}
                  onSecondIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/chart`);
                  }}
                />
                <VitalCard
                  title="Weight"
                  date={weight ? formatDateTime(weight.measure_new_date_time || weight.measure_date_time || weight.created_at) : ''}
                  value={weight ? parseFloat(weight.weight * 2.20462).toFixed(1) : '-'}
                  unit="lb"
                  pulse={null}
                  alertStatus={weightStatus}
                  noData={!weight}
                  isConfigured={isWeightConfigured}
                  onFirstIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/weight`);
                  }}
                  onSecondIconClick={() => {
                    navigate(`/practices/${practiceId}/patients/${patientId}/measurements/weight/chart`);
                  }}
                />
              </div>
            </div>

            {/* Summary of Measurement */}
            <div className="patient-details-card">
              <h3>Summary of Measurement</h3>
              <div className="patient-details-tabs-container">
                {avgTabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    className={`patient-details-tab-btn ${activeTab === index ? 'active' : ''}`}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}<br />
                    <span className="patient-details-tab-date">
                      {getSummaryRange(tab.id).end} ~ {getSummaryRange(tab.id).start}
                    </span>
                  </button>
                ))}
              </div>
              <div className="patient-details-measurement-grid">
                <div className="patient-details-measurement-card">
                  <h4>Blood Pressure</h4>
                  <p className="patient-details-measurement-value">
                    {avgTabs[activeTab]?.data?.blood_pressure?.systolic || '-'}
                    / {avgTabs[activeTab]?.data?.blood_pressure?.diastolic || '-'}
                  </p>
                  <p className="patient-details-measurement-unit">mmHg</p>
                </div>
                <div className="patient-details-measurement-card">
                  <h4>Blood Glucose</h4>
                  <p className="patient-details-measurement-value">
                    {avgTabs[activeTab]?.data?.blood_glucose || '-'}
                  </p>
                  <p className="patient-details-measurement-unit">mg/dl</p>
                </div>
                <div className="patient-details-measurement-card">
                  <h4>Weight</h4>
                  <p className="patient-details-measurement-value">
                    {avgTabs[activeTab]?.data?.weight
                      ? parseFloat(avgTabs[activeTab].data.weight * 2.20462).toFixed(1)
                      : '-'}
                  </p>
                  <p className="patient-details-measurement-unit">lb</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="patient-details-right-column">
            {/* Timer Section - hidden while practice is locked */}
            {!practiceIsLocked && (
              <div className="patient-details-card patient-details-timer-card">
                <div className="patient-details-timer-section-notes">
                  <div className="patient-details-timer-display-box">
                    <div className="patient-details-timer-value-large">{formatTimer(timer)}</div>
                    <div className="patient-details-timer-labels-box">
                      <span>Min</span>
                      <span>Sec</span>
                    </div>
                  </div>
                  <div className="patient-details-timer-input-boxes">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="patient-details-timer-input-box"
                      value={Math.floor(timer / 60)}
                      onChange={(e) => handleTimerMinutesChange(e.target.value)}
                    />
                    <strong>:</strong>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      className="patient-details-timer-input-box"
                      value={timer % 60}
                      onChange={(e) => handleTimerSecondsChange(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="patient-details-timer-pause-btn"
                    onClick={isRunning ? pauseTimer : startTimer}
                  >
                    <FontAwesomeIcon icon={isRunning ? faPause : faPlay} />
                  </button>
                </div>
              </div>
            )}
            {practiceIsLocked && (
              <div className="patient-details-card patient-details-notes-card">
                <p className="small text-muted mb-0" role="status">
                  Practice is locked. Session timer is paused; follow-up actions are not available.
                </p>
              </div>
            )}

            {/* Notes Section */}
            <div className="patient-details-card patient-details-notes-card">
              <div className="patient-details-notes-header">
                <h3>Notes</h3>
                {!practiceIsLocked && (
                  <span
                    className="patient-details-link-text"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/practices/${practiceId}/patients/${patientId}/follow-ups`);
                    }}
                  >
                    See All
                  </span>
                )}
              </div>

              {!practiceIsLocked && (
                <button
                  type="button"
                  className="patient-details-btn-full-width"
                  onClick={() => setShowFollowUpModal(true)}
                >
                  Add Follow Up
                </button>
              )}
              <div className="patient-details-notes-list">
                {follow_up_notes && follow_up_notes.length > 0 ? (
                  follow_up_notes.map((note, index) => (
                    <NoteItem
                      key={index}
                      author={`${note.last_name || ''}, ${note.first_name || ''}`.trim() || 'Unknown'}
                      date={note.date ? formatDateShort(note.date) : formatDateShort(note.created_at)}
                      text={note.content || ''}
                    />
                  ))
                ) : (
                  <div className="patient-details-empty-notes">
                    <p>No notes available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Today Section */}
            <div className="patient-details-card">
              <div className="patient-details-notes-header">
                <h3>Today</h3>
                <a href="#" className="patient-details-link-text">View All</a>
              </div>
              <div className="patient-details-event-box">
                Upcoming Events
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-up Modal */}
      <AddFollowUpModal
        isOpen={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        practiceId={practiceId}
        patientId={patientId}
        patientName={patientFullName}
        onSuccess={async () => {
          // New follow-up added: invalidate notes cache so latest data is fetched.
          const pid = patientData?.patient?.patients_table_id || patientData?.patient?.id || patientId;
          notesCacheRef.current.delete(`${practiceId}:${pid}`);
          detailsCacheRef.current.delete(`${practiceId}:${patientId}`);
          await fetchPatientDetails({ includeNotes: false, showLoader: false });
          // Only notes are refreshed here instead of full page payload.
          try {
            const followUpNotes = await fetchPatientNotes(patientData || {});
            setPatientData((prev) => ({
              ...(prev || {}),
              follow_up_notes: followUpNotes
            }));
          } catch (_) {
            // keep silent; details already refreshed
          }
        }}
        initialTimerValue={timer}
      />

      {/* Add Meter Modal */}
      {showAddMeterModal && (
        <div className="patient-details-modal-overlay" onClick={() => setShowAddMeterModal(false)}>
          <div className="patient-details-modal-content-wrapper-followup" onClick={(e) => e.stopPropagation()}>
            <div className="patient-details-modal-header-followup">
              <h3>Add Meter to Patient</h3>
              <button
                className="patient-details-modal-close-btn"
                onClick={() => {
                  setShowAddMeterModal(false);
                  setAddMeterForm({
                    model_number: '',
                    extension: '',
                    serial_number: '',
                    gateway_serial_number: ''
                  });
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="patient-details-modal-body-followup">
              <form onSubmit={handleAddMeter} style={{ width: '100%', padding: '20px' }}>
                <div className="patient-details-form-group">
                  <label>Model Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.model_number}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, model_number: e.target.value })}
                    placeholder="e.g., BG/123 or BP"
                    required
                  />
                </div>
                <div className="patient-details-form-group">
                  <label>Extension <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.extension}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, extension: e.target.value })}
                    placeholder="Extension ID"
                    required
                  />
                </div>
                <div className="patient-details-form-group">
                  <label>Serial Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.serial_number}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, serial_number: e.target.value })}
                    placeholder="Serial Number"
                    required
                  />
                </div>
                <div className="patient-details-form-group">
                  <label>Gateway Serial Number</label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.gateway_serial_number}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, gateway_serial_number: e.target.value })}
                    placeholder="Gateway Serial Number (optional)"
                  />
                </div>
                <div className="patient-details-modal-footer">
                  <button
                    type="button"
                    className="patient-details-btn-cancel"
                    onClick={() => {
                      setShowAddMeterModal(false);
                      setAddMeterForm({
                        model_number: '',
                        extension: '',
                        serial_number: '',
                        gateway_serial_number: ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="patient-details-btn-save"
                  >
                    Add Meter
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Book Appointment Modal */}
      {showAppointmentModal && (
        <div className="patient-details-modal-overlay" onClick={() => setShowAppointmentModal(false)}>
          <div className="patient-details-modal-content-wrapper-appointment" onClick={(e) => e.stopPropagation()}>
            <div className="patient-details-modal-header-appointment">
              <h3>Make an Appointment</h3>
              <button
                className="patient-details-modal-close-btn"
                onClick={() => {
                  setShowAppointmentModal(false);
                  setAppointmentData({
                    facility: '',
                    date: new Date().toISOString().split('T')[0],
                    startTime: '',
                    endTime: '',
                    provider: '',
                    caregiver: '',
                    visitType: '',
                    visitStatus: ''
                  });
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="patient-details-modal-body-appointment">
              <form>
                <div className="patient-details-appointment-form-grid">
                  <div className="patient-details-form-group">
                    <label>Facility</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.facility}
                      onChange={(e) => setAppointmentData({ ...appointmentData, facility: e.target.value })}
                    >
                      <option value="">Select Facility</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Provider</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.provider}
                      onChange={(e) => setAppointmentData({ ...appointmentData, provider: e.target.value })}
                    >
                      <option value="">Select Provider</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Date</label>
                    <div className="patient-details-date-input-wrapper">
                      <input
                        type="date"
                        className="patient-details-form-control patient-details-date-input"
                        value={appointmentData.date}
                        onChange={(e) => setAppointmentData({ ...appointmentData, date: e.target.value })}
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Caregiver</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.caregiver}
                      onChange={(e) => setAppointmentData({ ...appointmentData, caregiver: e.target.value })}
                    >
                      <option value="">Select Caregiver</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Start Time</label>
                    <div className="patient-details-date-input-wrapper">
                      <input
                        type="time"
                        className="patient-details-form-control patient-details-date-input patient-details-time-input"
                        value={appointmentData.startTime}
                        onChange={(e) => setAppointmentData({ ...appointmentData, startTime: e.target.value })}
                        placeholder="--:--"
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Visit Type</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.visitType}
                      onChange={(e) => setAppointmentData({ ...appointmentData, visitType: e.target.value })}
                    >
                      <option value="">Select Visit Type</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>End Time</label>
                    <div className="patient-details-date-input-wrapper">
                      <input
                        type="time"
                        className="patient-details-form-control patient-details-date-input patient-details-time-input"
                        value={appointmentData.endTime}
                        onChange={(e) => setAppointmentData({ ...appointmentData, endTime: e.target.value })}
                        placeholder="--:--"
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Visit Status</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.visitStatus}
                      onChange={(e) => setAppointmentData({ ...appointmentData, visitStatus: e.target.value })}
                    >
                      <option value="">Select Visit Status</option>
                    </select>
                  </div>
                </div>
                <div className="patient-details-modal-footer">
                  <button
                    type="button"
                    className="patient-details-btn-cancel"
                    onClick={() => {
                      setShowAppointmentModal(false);
                      setAppointmentData({
                        facility: '',
                        date: new Date().toISOString().split('T')[0],
                        startTime: '',
                        endTime: '',
                        provider: '',
                        caregiver: '',
                        visitType: '',
                        visitStatus: ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="patient-details-btn-save"
                    onClick={() => {
                      // Handle save appointment
                      setShowAppointmentModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="patient-details-detail-item">
      <span className="patient-details-detail-label">{label}:</span>
      <span className="patient-details-detail-value">{value}</span>
    </div>
  );
}

function VitalCard({ title, date, value, systolic, diastolic, unit, pulse, alertStatus = 'normal', bpStatus, noData, isConfigured = true, onCardClick, onFirstIconClick, onSecondIconClick }) {
  const numericPulse = pulse != null ? Number(pulse) : null;
  const showPulseIcon = numericPulse != null && !Number.isNaN(numericPulse) && (numericPulse < 60 || numericPulse > 100);
  // Strict rule: actions are enabled only when vital is assigned/configured.
  const iconsEnabled = Boolean(isConfigured);

  return (
    <div className={`patient-details-vital-card ${onCardClick ? 'patient-details-vital-card-clickable' : ''}`} onClick={onCardClick}>
      <div className="patient-details-vital-header">
        {title}
        {date && <p className="patient-details-vital-date">{date}</p>}
      </div>
      <div className="patient-details-vital-body">
        {noData ? (
          <>
            <p className="patient-details-vital-value-no-data">-</p>
            <p className="patient-details-vital-unit">{unit}</p>
          </>
        ) : (
          <>
            {title === 'Blood Pressure' ? (
              <p className="patient-details-vital-value">
                <span className={bpStatus?.sysStatus === 'high' ? 'alert-high' : bpStatus?.sysStatus === 'low' ? 'alert-low' : 'alert-normal'}>{systolic}</span>
                <span className="patient-details-vital-separator"> / </span>
                <span className={bpStatus?.diaStatus === 'high' ? 'alert-high' : bpStatus?.diaStatus === 'low' ? 'alert-low' : 'alert-normal'}>{diastolic}</span>
              </p>
            ) : (
              <p
                className={`patient-details-vital-value ${alertStatus === 'high'
                  ? 'alert-high'
                  : alertStatus === 'low'
                    ? 'alert-low'
                    : 'alert-normal'
                  }`}
              >
                {value}
              </p>
            )}
            <p className="patient-details-vital-unit">{unit}</p>
          </>
        )}
        <div className="patient-details-pulse-info">
          Pulse{' '}
          <span className={`patient-details-pulse-value ${numericPulse != null ? (numericPulse > 100 ? 'alert-high' : numericPulse < 60 ? 'alert-low' : 'alert-normal') : ''}`}>
            {pulse || '-'}
            {showPulseIcon ? <img src={pulseIcon} alt="pulse" className="patient-details-pulse-icon" /> : null}
          </span>
        </div>
      </div>
      <div className="patient-details-vital-footer">
        {noData && <span className="patient-details-no-data-text">No data</span>}
        <div className="patient-details-vital-icons">
          <button
            className={`patient-details-icon-btn ${!iconsEnabled ? 'patient-details-icon-btn-disabled' : ''}`}
            disabled={!iconsEnabled}
            onClick={(e) => {
              e.stopPropagation();
              if (iconsEnabled && onFirstIconClick) onFirstIconClick();
            }}
            title={!iconsEnabled ? 'Vital not configured' : (noData ? 'Add/view measurements' : 'View measurements')}
          >
            <FontAwesomeIcon icon={faFileText} />
          </button>
          <button
            className={`patient-details-icon-btn ${!iconsEnabled ? 'patient-details-icon-btn-disabled' : ''}`}
            disabled={!iconsEnabled}
            onClick={(e) => {
              e.stopPropagation();
              if (iconsEnabled && onSecondIconClick) onSecondIconClick();
            }}
            title={!iconsEnabled ? 'Vital not configured' : (noData ? 'View chart (no data yet)' : 'View chart')}
          >
            <FontAwesomeIcon icon={faAreaChart} />
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteItem({ author, date, text }) {
  return (
    <div className="patient-details-note-item">
      <div className="patient-details-note-meta">
        <span>{author}</span>
        <span className="patient-details-note-date">{date}</span>
      </div>
      <p>{text}</p>
    </div>
  );
}
